/* eslint-disable react/no-danger */
import React, { useState, useEffect } from 'react';

import queryString from 'query-string';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import OTPInput from 'react-otp-input';

import NavigationHeader from './NavigationHeader';
import hasApiErrors from '../../common/utils/utils';
import { login, handleLoginNavigation } from '../../store/actions/userState';
import { pageTitleConditions } from '../../constants';
import { resetPassword, updateOnBoardCustomer } from '../../api';
import { validatePassword } from '../../constants/UserConstants';
import {
  Button,
  InputFormField,
  ErrorMsg,
  OnBoardingBody,
  PageLoader,
  UnauthorizedHeader,
} from '../../common';
import ShowPasswordByIcon from '../../common/ShowPasswordByIcon';
import { MFAAuthenticationLogo2 } from '../../theme/images';
import Theme from '../../theme/Theme';

export default function CreateAccount() {
  const { register, handleSubmit, errors, watch } = useForm();
  const history = useHistory();
  const dispatch = useDispatch();
  const loader = useSelector((state) => state.userState.isLoading);
  const params = queryString.parse(history.location.search);
  const [apiError, setApiError] = useState({});
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState({ loader: false, type: 'button' });
  const [assignTo, setAssignTo] = useState(false);
  const [showSuccessMsg, setShowSuccessMsg] = useState(false);
  const [isPassowrdVisible, setIsPassowrdVisible] = useState(false);
  const [getEmail, setGetEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [showOtpScreen, setShowOtpScreen] = useState(false);

  const newDetails = [
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 're_assigned_user_email', label: 'Email Address' },
    { key: 'confirm_email', label: 'Confirm Email Address' },
  ];

  // Call the function if only page url is get changed (Code to add page title)
  useEffect(() => {
    pageTitleConditions(history.location);
    history.listen((location) => {
      pageTitleConditions(location);
    });
  }, [history]);

  useEffect(() => {
    if (params && params.type && params.type === 'new') {
      setAssignTo(true);
    } else {
      setAssignTo(false);
    }
  }, [params]);

  const savePassword = () => {
    setIsLoading({ loader: true, type: 'button' });
    const data = {
      ...formData,
      key: params.key,
    };
    const loginData = {
      ...formData,
      email: localStorage.getItem('email') || (params && params.email),
    };

    resetPassword(data).then((response) => {
      if (response && response.status === 400) {
        setApiError(response && response.data);
        setIsLoading({ loader: false, type: 'button' });
      } else if (response && response.status === 200) {
        dispatch(login(loginData, { customer: '' }))
          .then((loginResponse) => {
            if (
              loginResponse?.status === 200 ||
              loginResponse?.status === 201
            ) {
              if (loginResponse?.data?.mfa_required) {
                setShowOtpScreen(true);
                history.push('/mfa-setup', {
                  from: 'create-account',
                  loginResponse,
                  history,
                  customer: '',
                  id: params && params.id,
                });
                return;
              }
              handleLoginNavigation(
                loginResponse?.data,
                history,
                '',
                params && params.id,
              );
            }
          })
          .catch((error) => {
            setErrorMsg(
              error?.response?.data?.message ||
                'Invalid code. Please try again.',
            );
          })
          .finally(() => {
            setIsLoading({ loader: false, type: 'button' });
          });

        localStorage.removeItem('email');
        setIsLoading({ loader: false, type: 'button' });
      }
    });
  };

  const onSubmit = (data) => {
    setIsLoading({ loader: true, type: 'button' });
    const detail = {
      ...data,
      is_reassigned: true,
    };

    updateOnBoardCustomer(params.id, detail).then((response) => {
      if (response && response.status === 200) {
        setIsLoading({ loader: false, type: 'button' });
        setShowSuccessMsg(true);
        setGetEmail(data.re_assigned_user_email);
        localStorage.removeItem('email');
      }
      if (
        (response && response.status === 400) ||
        (response && response.status === 403)
      ) {
        setShowSuccessMsg(false);
        setApiError(response && response.data);
        setIsLoading({ loader: false, type: 'button' });
      }
      if (response && response.status === 401) {
        setShowSuccessMsg(false);
        setApiError(response && response.data);
        setIsLoading({ loader: false, type: 'button' });
        localStorage.removeItem('email');
      }
    });
  };

  const getHTML = (type) => {
    if (type === 'confirm_email') {
      return register({
        required: {
          value: true,
          message: 'This field is required.',
        },
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
          message: 'Invalid email address.',
        },
        validate: (value) =>
          value === watch('re_assigned_user_email') ||
          'The Email IDs do not match!',
      });
    }
    if (type === 're_assigned_user_email') {
      return register({
        required: {
          value: true,
          message: 'This field is required.',
        },
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
          message: 'Invalid email address.',
        },
      });
    }
    if (type === 'first_name' || type === 'last_name') {
      return register({
        required: {
          value: true,
          message: 'This field is required.',
        },
        pattern: {
          value: /^[0-9a-zA-Z_-]*$/i,
          message: 'Special characters are not allowed.',
        },
      });
    }
    return register({
      required: {
        value: true,
        message: 'This field is required.',
      },
    });
  };

  const handleOtpChange = (code) => {
    setOtp(code);
    if (errorMsg) setErrorMsg('');
  };

  const handleVerify = async () => {
    if (otp.length < 6) return;
    setErrorMsg('');

    setIsLoading({ loader: true, type: 'verify' });

    const detail = {
      email: localStorage.getItem('email') || (params && params.email),
      password: formData.password,
      otp,
    };
    dispatch(login(detail, { customer: '' }))
      .then((loginData) => {
        if (loginData?.status === 200 || loginData?.status === 201) {
          handleLoginNavigation(
            loginData?.data,
            history,
            '',
            params && params.id,
          );
        }
      })
      .catch((error) => {
        setOtp('');
        setErrorMsg(
          error?.response?.data?.message || 'Invalid code. Please try again.',
        );
      })
      .finally(() => {
        setIsLoading({ loader: false, type: 'verify' });
      });
  };

  const displayOTPScreen = () => {
    return (
      <div className="white-card-base panel panel-unauthorize-header">
        <p className="account-steps m-0">Step 1 of 6</p>
        <div style={{ color: 'black' }} className="fade-in">
          <div className="d-flex align-items-center mb-3">
            <img
              src={MFAAuthenticationLogo2}
              alt="MFA Authentication"
              className="me-3 mr-2"
              width="40"
              height="40"
            />
            <h2>Verify your code</h2>
          </div>
          <p className="mb-4">
            Enter the 6-digit code from your authenticator app to finalize
            setup.
          </p>

          <div className="d-flex justify-content-center mb-3" width="80%">
            <OTPInput
              value={otp}
              onChange={handleOtpChange}
              numInputs={6}
              inputType="tel"
              renderSeparator={null}
              shouldAutoFocus
              renderInput={(props) => (
                <input
                  {...props}
                  className="form-control text-center fw-bold fs-4 focus-ring focus-ring-primary border-secondary-subtle"
                  style={{
                    width: '48px',
                    height: '56px',
                    margin: '0 6px',
                    boxShadow: 'none',
                  }}
                />
              )}
            />
          </div>
          {errorMsg && (
            <ErrorMsg className="text-center text-danger mb-3">
              {errorMsg}
            </ErrorMsg>
          )}
          <div className="d-flex flex-column gap-3 mb-4 mt-2">
            <Button
              onClick={handleVerify}
              disabled={otp.length < 6}
              className="btn-primary mb-3 w-100"
            >
              {isLoading.loader && isLoading.type === 'verify' ? (
                <PageLoader color={Theme.white} type="button" />
              ) : (
                'Verify & Finish'
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <UnauthorizedHeader />
      {showSuccessMsg ? (
        ''
      ) : (
        <NavigationHeader
          bar="20"
          backStep={showSuccessMsg ? '' : '1'}
          showSuccessMsg={showSuccessMsg}
        />
      )}

      <OnBoardingBody className={showSuccessMsg ? 'grey-bg' : 'body-white'}>
        {!assignTo ? (
          showOtpScreen ? (
            displayOTPScreen()
          ) : (
            <div className="white-card-base panel panel-unauthorize-header">
              <p className="account-steps m-0">Step 1 of 6</p>
              <h3 className="page-heading ">
                Please set your password to create your account.
              </h3>
              <p className="info-text-gray m-0 ">
                If you’d like someone else to manage the Buy Box Experts account
                then you can reassign the setup process to them.
                <br />
                {params && params.multiple_customers === 'True' ? (
                  <div className="already-user-msg mt-2">
                    This email is associated with more than one account. Note
                    that the password you set here will be used for all your
                    accounts.
                  </div>
                ) : (
                  ''
                )}
              </p>
              <InputFormField className="mt-3">
                <label htmlFor="email">
                  Email Address
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Enter your  Email address"
                    defaultValue={
                      localStorage.getItem('email') || (params && params.email)
                    }
                    readOnly
                  />
                </label>
              </InputFormField>
              <InputFormField className="mt-3">
                <label htmlFor="password">
                  Password
                  <input
                    className="form-control"
                    type={isPassowrdVisible ? 'text' : 'password'}
                    placeholder="Enter your Password"
                    name="password"
                    onKeyPress={(event) => {
                      if (event.key === 'Enter') {
                        savePassword();
                      }
                    }}
                    onChange={(event) => {
                      setFormData({ [event.target.name]: event.target.value });
                      setApiError({
                        ...apiError,
                        [event.target.name]:
                          validatePassword(event.target.value) || '',
                      });
                    }}
                  />
                  <ShowPasswordByIcon
                    isPasswordEyeOpen={isPassowrdVisible}
                    setIsPasswordEyeOpen={setIsPassowrdVisible}
                    value={formData.password}
                  />
                </label>
              </InputFormField>
              <ErrorMsg>
                {apiError?.password &&
                  (Array.isArray(apiError.password)
                    ? apiError.password[0]
                    : apiError.password)}
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      apiError?.key &&
                      (Array.isArray(apiError.key)
                        ? apiError.key[0]
                        : apiError.key),
                  }}
                />
              </ErrorMsg>
              <Button
                className="btn-primary w-100 mt-4"
                disabled={hasApiErrors(apiError) || loader || isLoading.loader}
                onClick={() => savePassword()}
              >
                {loader || (isLoading.loader && isLoading.type === 'button') ? (
                  <PageLoader color="#FFF" type="button" />
                ) : (
                  'Continue'
                )}
              </Button>
            </div>
          )
        ) : (
          <>
            {showSuccessMsg ? (
              <div className="white-card-base account-reassign">
                <h3 className="page-heading ">Account Setup Reassigned</h3>
                <p className="information-text m-0 ">
                  You have successfully reassigned the account setup process to
                  the following email address:
                </p>
                <div className="complete-steps mb-2">{getEmail}</div>
                <p className="reach-out m-0 ">
                  If you need any assistance please reach out to
                  <br />
                  <a
                    className="reach-out-link"
                    target="_BLANK"
                    rel="noopener noreferrer"
                    href="https://www.buyboxexperts.com/"
                  >
                    onboarding@buyboxexperts.com
                  </a>
                </p>
              </div>
            ) : (
              <div className="white-card-base panel pt-5">
                <h3 className="page-heading pt-5 mt-4">
                  Re-assign account setup
                </h3>
                <p className="information-text m-0 ">
                  <div className="sub-information">
                    {' '}
                    If you’d like someone else to administer the Buy Box Experts
                    account then you can reassign the setup process to them.
                  </div>

                  <span className="note">
                    Note: Only the nominated email address will be able to add
                    and remove access for other members of your team.
                  </span>
                </p>
                <form onSubmit={handleSubmit(onSubmit)}>
                  {newDetails.map((item) => (
                    <InputFormField key={item.key} className="mt-3">
                      <label htmlFor={item.key}>
                        {item.label}
                        <br />
                        <input
                          className={
                            errors && errors.email
                              ? 'form-control'
                              : 'form-control mt-2'
                          }
                          type="text"
                          placeholder={`Enter ${item.label}`}
                          onChange={(event) =>
                            setApiError({
                              ...apiError,
                              detail: '',
                              [event.target.name]: '',
                            })
                          }
                          id={item.key}
                          name={item.key}
                          ref={getHTML(item.key)}
                        />
                      </label>
                      <ErrorMsg>
                        {errors && errors[item.key] && errors[item.key].message}
                      </ErrorMsg>
                      <ErrorMsg>
                        {apiError &&
                          apiError[item.key] &&
                          apiError[item.key][0]}
                      </ErrorMsg>
                    </InputFormField>
                  ))}
                  <ErrorMsg>{apiError && apiError.detail}</ErrorMsg>
                  <ErrorMsg>
                    {apiError &&
                      apiError.non_field_errors &&
                      apiError.non_field_errors[0]}
                  </ErrorMsg>
                  <Button className="btn-primary w-100 mt-4">
                    {' '}
                    {isLoading.loader && isLoading.type === 'button' ? (
                      <PageLoader color="#FFF" type="button" />
                    ) : (
                      'Confirm'
                    )}
                  </Button>
                </form>
              </div>
            )}
          </>
        )}
      </OnBoardingBody>
    </>
  );
}
